// Pestaña «Otras tiendas» de la ficha: dónde más compró el cliente dentro de la misma cadena.
import { useMemo, useState } from 'react';
import type { PortalCustomerOtherStore } from '@/api/services/retail-wizard/contract';
import { formatDateMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { PENDING_CELL } from '../lib/customersFormat';
import {
    DEFAULT_OTHER_STORES_SORT,
    nextDetailSort,
    sortOtherStores,
    type OtherStoresSort,
    type OtherStoresSortKey,
} from '../lib/portalCustomerFormat';
import { DetailCard } from './DetailCard';

const TEXT_SORT_KEYS: OtherStoresSortKey[] = ['storeName'];

const COLUMNS: DataTableColumn<PortalCustomerOtherStore, OtherStoresSortKey>[] = [
    {
        key: 'storeName',
        label: 'Tienda',
        sortKey: 'storeName',
        cellClassName: () => 'font-medium',
        cell: (store) => store.storeName ?? PENDING_CELL,
    },
    {
        key: 'purchasesCount',
        label: 'Compras',
        width: '84px',
        numeric: true,
        sortKey: 'purchasesCount',
        cellClassName: () => 'font-bold',
        cell: (store) => store.purchasesCount.toLocaleString('es-MX'),
    },
    {
        key: 'totalSoldCents',
        label: 'Total $',
        width: '80px',
        numeric: true,
        sortKey: 'totalSoldCents',
        cellClassName: () => 'font-bold',
        cell: (store) => formatMXN(store.totalSoldCents / 100),
    },
    {
        key: 'lastPurchaseAt',
        label: 'Última',
        width: '84px',
        note: 'Última compra en esa tienda',
        sortKey: 'lastPurchaseAt',
        cellClassName: () => 'text-[10.5px] tabular-nums text-muted-foreground',
        cell: (store) => formatDateMx(store.lastPurchaseAt),
    },
];

export function CustomerOtherStoresTab({ stores }: { stores: PortalCustomerOtherStore[] }) {
    const [sort, setSort] = useState<OtherStoresSort>(DEFAULT_OTHER_STORES_SORT);
    const rows = useMemo(() => sortOtherStores(stores, sort), [stores, sort]);

    return (
        <DetailCard
            title="Otras tiendas donde ha comprado"
            aside={`${String(stores.length)} ${stores.length === 1 ? 'tienda' : 'tiendas'}`}
            flush
        >
            {stores.length === 0 ? (
                <p className="p-3 text-center text-[10.5px] italic text-muted-foreground">
                    Solo te ha comprado a ti.
                </p>
            ) : (
                <DataTable
                    columns={COLUMNS}
                    rows={rows}
                    rowKey={(store) => store.storeId}
                    caption="Otras tiendas de tu cadena"
                    sort={sort}
                    onSortToggle={(key) => {
                        setSort(nextDetailSort(sort, key, TEXT_SORT_KEYS));
                    }}
                    className="flex-none"
                />
            )}
        </DetailCard>
    );
}
